import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const StatusChangeDialog = ({
  open,
  onClose,
  onConfirm,
  sourceStage,
  targetStage,
  leadName,
  validationResult,
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [reasonLost, setReasonLost] = useState('');
  const [transitionNote, setTransitionNote] = useState('');
  const [overrideValidation, setOverrideValidation] = useState(false);

  const isLostStage = targetStage?.id === 'lost';
  const requiresReasonLost = validationResult?.requiresInput?.includes('reasonLost') || isLostStage;
  const hasValidationWarning = validationResult && !validationResult.valid;

  useEffect(() => {
    if (open && targetStage) {
      setSelectedStatus(targetStage.statuses[0] || '');
      setReasonLost('');
      setTransitionNote('');
      setOverrideValidation(false);
    }
  }, [open, targetStage]);

  const handleConfirm = () => {
    if (requiresReasonLost && !reasonLost.trim()) return;
    if (hasValidationWarning && !overrideValidation) return;

    onConfirm({
      newStatus: selectedStatus,
      reasonLost: requiresReasonLost ? reasonLost : null,
      transitionNote: transitionNote || null,
      validationOverride: overrideValidation,
    });
  };

  const canConfirm = () => {
    if (requiresReasonLost && !reasonLost.trim()) return false;
    if (hasValidationWarning && !overrideValidation) return false;
    return !!selectedStatus;
  };

  if (!targetStage) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {isLostStage && <WarningAmberIcon color="warning" />}
          <Typography variant="h6">
            Move Lead to {targetStage.label}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {leadName && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Moving <strong>{leadName}</strong> from{' '}
            <strong>{sourceStage?.label || 'Unknown'}</strong> to{' '}
            <strong>{targetStage.label}</strong>
          </Typography>
        )}

        {/* Validation warning */}
        {hasValidationWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {validationResult.reason}
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideValidation}
                  onChange={(e) => setOverrideValidation(e.target.checked)}
                  size="small"
                />
              }
              label="Proceed anyway"
              sx={{ display: 'block', mt: 1 }}
            />
          </Alert>
        )}

        {/* Sub-status selection (only if more than one option) */}
        {targetStage.statuses.length > 1 && (
          <TextField
            select
            fullWidth
            label="Select Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            margin="dense"
            sx={{ mb: 2 }}
          >
            {targetStage.statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Reason lost (required for lost stage) */}
        {requiresReasonLost && (
          <TextField
            fullWidth
            label="Reason Lost *"
            placeholder="Why was this lead lost?"
            value={reasonLost}
            onChange={(e) => setReasonLost(e.target.value)}
            margin="dense"
            error={requiresReasonLost && !reasonLost.trim()}
            helperText={requiresReasonLost && !reasonLost.trim() ? 'Reason is required' : ''}
            sx={{ mb: 2 }}
          />
        )}

        {/* Optional note */}
        <TextField
          fullWidth
          label="Note (optional)"
          placeholder="Add a note about this status change..."
          value={transitionNote}
          onChange={(e) => setTransitionNote(e.target.value)}
          margin="dense"
          multiline
          rows={2}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm()}
          color={isLostStage ? 'error' : 'primary'}
        >
          {isLostStage ? 'Mark as Lost' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusChangeDialog;
